import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Building2, Gem, TrendingUp, BarChart3 } from 'lucide-react';
import { BRAND } from '@/brand';

interface Navbar2Props {
    bgColor?: string;
    logoTo?: string;
    signupTo?: string;
    className?: string;
}

const IS_CORP = BRAND === 'corporate';
const ACCENT = IS_CORP ? '#C76B12' : '#0C447C';      // link hover / text accent
const ACCENT_SOLID = IS_CORP ? '#E8890B' : '#1976D2'; // primary CTA fill
// Brand-tinted glow under the primary CTA (amber on corporate, blue on consulting).
const CTA_SHADOW = IS_CORP ? '0 10px 22px -12px rgba(240,138,36,.6)' : '0 10px 22px -12px rgba(25,118,210,.55)';
const LOGO = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png';

type Tone = 'y' | 'a' | 'd';
type DropItem = { to: string; title: string; desc: string; tone: Tone; icon: any };

const SERVICES: DropItem[] = IS_CORP ? [
    { to: '/accounting', title: 'Accounting & Tax', desc: 'Bookkeeping, VAT & corporate tax', tone: 'y', icon: FileText },
    { to: '/corporate', title: 'Corporate Services', desc: 'Licence, visas, PRO & banking', tone: 'a', icon: Building2 },
    { to: '/private', title: 'Conncct Private', desc: 'Multi-entity & family office · by invitation', tone: 'd', icon: Gem },
] : [
    { to: '/services/financial-advisory', title: 'Financial Advisory', desc: 'Strategy, modeling, valuation & decks', tone: 'a', icon: BarChart3 },
    { to: '/capital-raising', title: 'Capital Raising', desc: 'Get investor-ready and raise', tone: 'a', icon: TrendingUp },
    { to: '/private', title: 'Conncct Private', desc: 'Family office & private clients', tone: 'd', icon: Gem },
];

const PRICING: DropItem[] = IS_CORP ? [
    { to: '/accounting/pricing', title: 'Accounting pricing', desc: 'Free check, then your exact price', tone: 'y', icon: FileText },
    { to: '/corporate/pricing', title: 'Corporate pricing', desc: 'Plans, or build a custom setup', tone: 'a', icon: Building2 },
    { to: '/private', title: 'Conncct Private', desc: 'Priced by mandate, not by plan', tone: 'd', icon: Gem },
] : [
    { to: '/capital-raising/pricing', title: 'Consulting pricing', desc: 'Plans & tiers for every stage', tone: 'a', icon: TrendingUp },
    { to: '/private', title: 'Conncct Private', desc: 'Priced by mandate, not by plan', tone: 'd', icon: Gem },
];

const RESOURCES = [
    { to: '/about', label: 'About' },
    { to: '/partner', label: 'Partner with us' },
    // Consulting-only resources (AI practice + the Grow & Raise expert network).
    ...(IS_CORP ? [] : [
        { to: '/services/ai-technology', label: 'AI & Technology' },
        { to: '/sprints', label: 'Expert Sprints' },
        { to: '/experts', label: 'Join as an Expert' },
    ]),
    { to: '/careers', label: 'Careers' },
    { to: '/faq', label: 'FAQs' },
    // Public info pages that would otherwise only be reachable from the footer.
    { to: '/contact', label: 'Contact' },
    { to: '/privacy', label: 'Privacy' },
];

// Dropdown icon chips — amber/orange on corporate, blue on the consulting brand.
const toneCls: Record<Tone, string> = IS_CORP ? {
    y: 'bg-amber-50 text-amber-600',
    a: 'bg-orange-100 text-orange-700',
    d: 'bg-slate-900 text-amber-300',
} : {
    y: 'bg-blue-50 text-blue-600',
    a: 'bg-sky-100 text-sky-700',
    d: 'bg-slate-900 text-blue-300',
};

// Row hover in the dropdowns. Both strings are written out in full rather than
// built at runtime so Tailwind actually generates the two arbitrary classes.
const HOVER_ROW = IS_CORP ? 'hover:bg-[#F6F1E4]' : 'hover:bg-[#E9F1FA]';

export default function Navbar2({ logoTo, signupTo = '/signup', className = '' }: Navbar2Props) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // The "Corporate" wordmark belongs to the Corporate Services section only — the
    // rest of the corporate brand (accounting, about, careers) reads plain "Conncct".
    const showCorpWord = IS_CORP && /^\/corporate(\/|$)/.test(pathname);
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [loggedIn, setLoggedIn] = useState(false);
    const timers = useRef<Record<string, number>>({});

    const openDrop = (name: string) => {
        const tm = timers.current[name];
        if (tm) { clearTimeout(tm); delete timers.current[name]; }
        setOpenMenu(name);
    };
    const scheduleClose = (name: string) => {
        if (timers.current[name]) clearTimeout(timers.current[name]);
        timers.current[name] = window.setTimeout(() => {
            setOpenMenu(prev => (prev === name ? null : prev));
            delete timers.current[name];
        }, 180) as unknown as number;
    };

    const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en');

    const isLoggedIn = () => {
        const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
        if (!userStr) return false;
        const token =
            localStorage.getItem('token') ||
            sessionStorage.getItem('token') ||
            (() => { try { const a = localStorage.getItem('authTokens') || sessionStorage.getItem('authTokens'); return a ? JSON.parse(a).idToken : null; } catch { return null; } })();
        return !!(userStr && token);
    };

    useEffect(() => {
        setLoggedIn(isLoggedIn());
        return () => { Object.values(timers.current).forEach(clearTimeout); };
    }, []);

    const handleLogoClick = (e: React.MouseEvent) => {
        e.preventDefault();
        // The logo always goes to the public landing page — never bounce visitors
        // (even logged-in ones) to the dashboard.
        navigate(logoTo || '/');
    };

    const chevron = (open: boolean) => (
        <svg className={`w-2.5 h-2.5 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 11 7" fill="none">
            <path d="M1 1l4.5 4.5L10 1" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
    );

    const RichMenu = ({ name, items }: { name: string; items: DropItem[] }) => (
        <div
            className="relative flex items-center"
            onMouseEnter={() => openDrop(name)}
            onMouseLeave={() => scheduleClose(name)}
        >
            <button
                type="button"
                aria-haspopup="true"
                aria-expanded={openMenu === name}
                onClick={() => setOpenMenu(openMenu === name ? null : name)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap transition-colors"
                style={{ color: openMenu === name ? ACCENT : undefined }}
            >
                {name === 'services' ? 'Services' : 'Pricing'} {chevron(openMenu === name)}
            </button>
            <div className={`absolute left-1/2 top-full mt-1.5 w-[320px] bg-white border border-gray-100 rounded-2xl shadow-[0_24px_60px_-24px_rgba(23,38,58,0.35)] p-2.5 z-[120] origin-top transition-all duration-200 ease-out -translate-x-1/2 ${openMenu === name ? 'opacity-100 visible translate-y-0 pointer-events-auto' : 'opacity-0 invisible translate-y-2 pointer-events-none'}`}>
                {items.map(it => {
                    const Icon = it.icon;
                    return (
                        <Link
                            key={it.to + it.title}
                            to={it.to}
                            onClick={() => setOpenMenu(null)}
                            className={`flex items-start gap-3 p-2.5 rounded-xl ${HOVER_ROW} transition-colors group`}
                        >
                            <span className={`w-8 h-8 rounded-lg grid place-items-center flex-none mt-0.5 ${toneCls[it.tone]}`}>
                                <Icon className="w-4 h-4" />
                            </span>
                            <span className="min-w-0">
                                {/* Plain spans, not <b>/<em>: the nav renders inside page wrappers
                                    (.l7, .ht) whose element-level rules would otherwise recolour
                                    these rows — .l7 em turned every description accent-orange. */}
                                <span className="block text-[13.5px] font-extrabold text-[#17263A] leading-tight group-hover:text-[color:var(--acc)]" style={{ ['--acc' as any]: ACCENT }}>{it.title}</span>
                                <span className="block text-[11.5px] text-[#6E7888] font-semibold leading-snug mt-0.5">{it.desc}</span>
                            </span>
                        </Link>
                    );
                })}
                <span className="block border-t border-gray-100 mt-1.5 pt-2.5 px-2.5 text-[11.5px] font-bold text-[#6E7888]">
                    {IS_CORP ? 'Government fees always at cost.' : 'Fixed scopes. Never a % of your raise.'} <Link to="/faq" onClick={() => setOpenMenu(null)} className="font-extrabold" style={{ color: ACCENT }}>See FAQ →</Link>
                </span>
            </div>
        </div>
    );

    return (
        <>
            <header className={`sticky top-0 z-50 flex items-center justify-between gap-4 px-4 md:px-6 lg:px-8 py-3 backdrop-blur border-b border-[#EAE3D4] ${IS_CORP ? 'bg-[#FFFDF7]/90' : 'bg-white/90'} ${className}`}>
                {/* Logo — reads "Conncct Corporate" only inside the Corporate Services pages */}
                <a href="/" onClick={handleLogoClick} className="flex items-center cursor-pointer shrink-0">
                    <img src={LOGO} alt={showCorpWord ? 'Conncct Corporate' : 'Conncct'} className="h-6 md:h-7 w-auto" />
                    {showCorpWord && <span className="ml-2 text-[13px] md:text-[15px] font-extrabold tracking-tight text-[#C76B12]">Corporate</span>}
                </a>

                {/* Center nav */}
                <nav className="hidden lg:flex flex-1 items-center justify-center gap-7 text-[14.5px] font-bold text-[#17263A]">
                    <RichMenu name="services" items={SERVICES} />
                    <RichMenu name="pricing" items={PRICING} />
                    <Link to="/how-it-works" className="whitespace-nowrap transition-colors hover:text-[color:var(--acc)]" style={{ ['--acc' as any]: ACCENT }}>How it works</Link>

                    {/* Resources */}
                    <div className="relative flex items-center" onMouseEnter={() => openDrop('resources')} onMouseLeave={() => scheduleClose('resources')}>
                        <button
                            type="button"
                            aria-haspopup="true"
                            aria-expanded={openMenu === 'resources'}
                            onClick={() => setOpenMenu(openMenu === 'resources' ? null : 'resources')}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap transition-colors"
                            style={{ color: openMenu === 'resources' ? ACCENT : undefined }}
                        >
                            {t('nav.resources', 'Resources')} {chevron(openMenu === 'resources')}
                        </button>
                        <div className={`absolute left-1/2 top-full mt-1.5 w-48 bg-white border border-gray-100 rounded-2xl shadow-[0_24px_60px_-24px_rgba(23,38,58,0.35)] p-2 z-[120] origin-top transition-all duration-200 ease-out -translate-x-1/2 ${openMenu === 'resources' ? 'opacity-100 visible translate-y-0 pointer-events-auto' : 'opacity-0 invisible translate-y-2 pointer-events-none'}`}>
                            {RESOURCES.map(r => (
                                <Link key={r.to} to={r.to} onClick={() => setOpenMenu(null)} className={`block px-3 py-2 rounded-lg text-[13.5px] font-bold text-[#17263A] ${HOVER_ROW} transition-colors`}>{r.label}</Link>
                            ))}
                        </div>
                    </div>
                </nav>

                {/* Right actions */}
                <div className="hidden lg:flex items-center gap-3 shrink-0">
                    {loggedIn ? (
                        <>
                            <button onClick={toggleLanguage} className="font-extrabold text-[13px] text-[#17263A]">{t('nav.arabic', 'العربية')}</button>
                            <button onClick={() => (window.location.href = '/dashboard')} className="text-white rounded-xl px-4 py-2.5 font-extrabold text-[13px] whitespace-nowrap" style={{ background: ACCENT_SOLID, boxShadow: CTA_SHADOW }}>
                                {t('nav.goToDashboard', 'Go to Dashboard')} ›
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="text-[11.5px] text-[#6E7888] font-semibold whitespace-nowrap">Have an account? <Link to="/login" className="font-extrabold" style={{ color: ACCENT }}>Login →</Link></span>
                            <button onClick={toggleLanguage} className="font-extrabold text-[13px] text-[#17263A] hover:text-[#42505F] transition-colors">{t('nav.arabic', 'العربية')}</button>
                            <Link to="/contact" className="bg-white border border-[#EAE3D4] text-[#17263A] rounded-xl px-3.5 py-2.5 font-extrabold text-[13px] whitespace-nowrap hover:border-[#17263A] hover:-translate-y-0.5 transition-all duration-200">Book a free call</Link>
                            <Link to={signupTo} className="text-white rounded-xl px-4 py-2.5 font-extrabold text-[13px] whitespace-nowrap hover:-translate-y-0.5 transition-transform duration-200" style={{ background: ACCENT_SOLID, boxShadow: CTA_SHADOW }}>Get started ›</Link>
                        </>
                    )}
                </div>

                {/* Burger */}
                <button className="lg:hidden p-2 text-[#17263A]" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
                    {mobileOpen ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                    )}
                </button>
            </header>

            {/* Mobile menu */}
            {mobileOpen && (
                <div className={`lg:hidden ${IS_CORP ? 'bg-[#FFFDF7]' : 'bg-white'} border-b border-[#EAE3D4] px-5 py-4 flex flex-col gap-1`}>
                    <span className="text-[10.5px] font-extrabold tracking-[0.14em] uppercase text-[#6E7888] pt-2 pb-1">Services</span>
                    {SERVICES.map(it => <Link key={it.to + it.title} to={it.to} onClick={() => setMobileOpen(false)} className="py-2.5 font-bold text-[#17263A] border-b border-[#EAE3D4]">{it.title}</Link>)}
                    <span className="text-[10.5px] font-extrabold tracking-[0.14em] uppercase text-[#6E7888] pt-3 pb-1">Pricing</span>
                    {PRICING.map(it => <Link key={'m' + it.to + it.title} to={it.to} onClick={() => setMobileOpen(false)} className="py-2.5 font-bold text-[#17263A] border-b border-[#EAE3D4]">{it.title}</Link>)}
                    <span className="text-[10.5px] font-extrabold tracking-[0.14em] uppercase text-[#6E7888] pt-3 pb-1">Company</span>
                    <Link to="/how-it-works" onClick={() => setMobileOpen(false)} className="py-2.5 font-bold text-[#17263A] border-b border-[#EAE3D4]">How it works</Link>
                    {RESOURCES.map(r => <Link key={'m' + r.to} to={r.to} onClick={() => setMobileOpen(false)} className="py-2.5 font-bold text-[#17263A] border-b border-[#EAE3D4]">{r.label}</Link>)}

                    <button onClick={() => { toggleLanguage(); setMobileOpen(false); }} className="py-3 font-bold text-[#17263A] text-start">{t('nav.arabic', 'العربية')}</button>
                    {loggedIn ? (
                        <button onClick={() => (window.location.href = '/dashboard')} className="text-white rounded-xl px-6 py-3 font-extrabold text-center mt-1" style={{ background: ACCENT_SOLID }}>{t('nav.goToDashboard', 'Go to Dashboard')} ›</button>
                    ) : (
                        <>
                            <Link to="/login" onClick={() => setMobileOpen(false)} className="py-2.5 font-extrabold" style={{ color: ACCENT }}>Login →</Link>
                            <Link to="/contact" onClick={() => setMobileOpen(false)} className="bg-white border border-[#EAE3D4] text-[#17263A] rounded-xl px-6 py-3 font-extrabold text-center mt-1">Book a free call</Link>
                            <Link to={signupTo} onClick={() => setMobileOpen(false)} className="text-white rounded-xl px-6 py-3 font-extrabold text-center mt-1" style={{ background: ACCENT_SOLID }}>Get started ›</Link>
                        </>
                    )}
                </div>
            )}
        </>
    );
}
